import { TemplateType, type ITemplateAssignedType, type TemplateInfo } from '~/types/template';
import type { IShopifyPageAssigned } from '~/modules/shopify/types';

import { useQueryShopifyTemplateAssigned } from '~/modules/shopify/hooks/useQueryShopifyTemplateAssigned';
import { isMobileViewport, useTemplateUnassigned } from '~/presentation/common';
import { useAssignedStore } from '~/stores/assigned';
import { useShopInfo } from '~/modules/apps';
import { isBrowser } from '~/utils';
import { useRef } from 'react';

export interface IOpenPreview {
  templateInfo: TemplateInfo;
  searchParam?: string;
}
export const useCampaignVariantPreview = () => {
  let openWindow: any;
  const isPreviewing = useRef(false);

  const { shopURL } = useShopInfo();
  const { fetchTemplateUnassigned } = useTemplateUnassigned();

  const getUnassignedByType = useAssignedStore((state) => state.getUnassignedByType);
  const getAssignedByType = useAssignedStore((state) => state.getAssignedByType);
  const setAssignedByType = useAssignedStore((state) => state.setAssignedByType);
  const setLoadingByType = useAssignedStore((state) => state.setLoadingByType);

  const { fetchManual } = useQueryShopifyTemplateAssigned();

  const getUrlFromAssigned = (
    assigned: IShopifyPageAssigned | undefined,
    targetPageIds?: string[],
  ) => {
    if (!assigned) return undefined;
    if (targetPageIds?.length) {
      return assigned.data?.find((item) => item.id === targetPageIds[0])?.url;
    }
    return assigned.url;
  };

  const getLinkPreview = async ({ suffix, type }: TemplateInfo, targetPageIds?: string[]) => {
    if (type === TemplateType.HOME) {
      return shopURL;
    }

    const templateAssignedType = type as unknown as ITemplateAssignedType;
    let unassigned = getUnassignedByType(templateAssignedType);
    const templateAssigned = getAssignedByType(templateAssignedType);
    const storedUrl = getUrlFromAssigned(templateAssigned?.[suffix], targetPageIds);
    if (storedUrl) return storedUrl;

    const { data } = await fetchManual({
      type,
      clearCache: false,
    });

    setAssignedByType(templateAssignedType, data);
    setLoadingByType(templateAssignedType, false);
    unassigned = fetchTemplateUnassigned({
      templateAssigned: data,
      activePageType: templateAssignedType,
    });
    return getUrlFromAssigned(data?.[suffix], targetPageIds) ?? unassigned?.url;
  };

  const openPreview = async ({ templateInfo, searchParam }: IOpenPreview) => {
    createBlankWindowPreview();
    const urlPreview = await getLinkPreview(templateInfo);
    if (!urlPreview) {
      closePreviewWindow();
      return;
    }

    const url = new URL(urlPreview);
    if (searchParam) {
      url.search = searchParam;
    }

    setPreviewWindowUrl(url.href);
    resetPreviewStatus();
  };

  const openPreviewByUrl = (url: string) => {
    setPreviewWindowUrl(url);
    resetPreviewStatus();
  };

  // Preview status
  const resetPreviewStatus = () => {
    isPreviewing.current = false;
  };
  const getPreviewStatus = () => {
    return isPreviewing.current;
  };
  const closePreviewWindow = () => {
    openWindow?.close();
    isPreviewing.current = false;
  };
  const setPreviewWindowUrl = (url: string) => {
    // Mobile webview has no separate tab (createBlankWindowPreview opens none), so
    // navigate the current view to the preview instead.
    if (isMobileViewport()) {
      (window.top ?? window).location.href = url;
      return;
    }
    if (openWindow) {
      openWindow.location.href = url;
    }
  };
  const createBlankWindowPreview = () => {
    isPreviewing.current = true;
    // `window.open('', '_blank')` returns null in the Shopify mobile webview; skip it and
    // let setPreviewWindowUrl navigate in place once the URL is resolved.
    if (isMobileViewport()) return;
    openWindow = window.open('', '_blank');
    if (!isBrowser()) return;
    openWindow.document.write(
      `
        <div style="font-family: monospace; margin: 20px;">
          <p style="text-align: left; font-size: 24px; color: #333;">[GemX: CRO & A/B Test] previewing...</p>
        <div>
      `,
    );
  };

  return {
    openPreviewByUrl,
    openPreview,
    getLinkPreview,
    getPreviewStatus,
    closePreviewWindow,
    createBlankWindowPreview,
  };
};
